import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowPathIcon,
  BellAlertIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import {
  WindAlertAdminService,
  WindAlertEvaluation,
  TriggerWindAlertRequest,
  DigestEntry,
} from "@/services/WindAlertAdminService";
import { UserSearchInput } from "@/components/ui/UserSearchInput";
import SpotSearchInput from "@/components/spotimages/SpotSearchInput";
import { User } from "@/common/model/User";
import { SpotSearchResult } from "@/common/model/spotimages/SpotImage";
import { ENDPOINTS } from "@/constants/api";

const MODEL_OPTIONS = [
  { key: "gfs",          label: "GFS (global, 22 km)" },
  { key: "ecmwf_ifs",    label: "ECMWF IFS (global)" },
  { key: "icon_global",  label: "ICON (global)" },
  { key: "icon_d2",      label: "ICON-D2 (Germany, 2 km)" },
  { key: "arome_hd",     label: "AROME-HD (France, 1.3 km)" },
  { key: "arome",        label: "AROME (France)" },
  { key: "hrrr",         label: "HRRR (US, 3 km)" },
  { key: "ukmo_uk",      label: "UKMO (UK)" },
  { key: "knmi_nl",      label: "KNMI (Netherlands)" },
  { key: "knmi_europe",  label: "KNMI Harmonie (Europe)" },
  { key: "metno_nordic", label: "MET.no Nordic" },
  { key: "gem_hrdps",    label: "HRDPS (Canada)" },
];

type Tab = "evaluations" | "trigger";

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(iso); }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "2-digit",
    });
  } catch { return String(iso); }
}

function defaultPeakWhen(): string {
  const d = new Date(Date.now() + 24 * 3600 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const CARDINAL_LABELS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
const DIRECTION_ARROWS = ["↓", "↓", "↙", "↙", "←", "←", "↖", "↖", "↑", "↑", "↗", "↗", "→", "→", "↘", "↘"];
function degreesToCardinal(deg: number): string { return CARDINAL_LABELS[((deg % 360 + 360) % 360 + 11) / 22 % 16 | 0]; }
function degreesToArrow(deg: number): string { return DIRECTION_ARROWS[((deg % 360 + 360) % 360 + 11) / 22 % 16 | 0]; }

const PAGE_SIZE = 25;

const WindAlertsPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>("evaluations");
  const [evaluations, setEvaluations] = useState<WindAlertEvaluation[]>([]);
  const [evalTotal, setEvalTotal] = useState(0);
  const [evalPage, setEvalPage] = useState(0);
  const [evalFilter, setEvalFilter] = useState<{ status: string; search: string }>({ status: "", search: "" });
  const [evalSort, setEvalSort] = useState<{ sortBy: string; sortDir: "asc" | "desc" }>({ sortBy: "createdAt", sortDir: "desc" });
  const [pageInput, setPageInput] = useState("");
  const [loading, setLoading] = useState(true);

  // Trigger form state
  const [triggerForm, setTriggerForm] = useState<TriggerWindAlertRequest>({
    userId: 0, spotId: 0, modelKey: "gfs", timestamp: 0, knots: 22, gust: 28, reminder: false,
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedSpot, setSelectedSpot] = useState<SpotSearchResult | null>(null);
  const [peakWhen, setPeakWhen] = useState<string>(() => defaultPeakWhen());
  const [triggerStatus, setTriggerStatus] = useState<{ kind: "ok" | "err"; message: string } | null>(null);
  const [triggering, setTriggering] = useState(false);

  const handleUserSelect = useCallback((u: User) => {
    setSelectedUser(u);
    setTriggerForm((prev) => ({ ...prev, userId: u.id }));
  }, []);
  const handleSpotSelect = useCallback((s: SpotSearchResult) => {
    setSelectedSpot(s);
    setTriggerForm((prev) => ({ ...prev, spotId: s.id }));
  }, []);

  const loadEvaluations = useCallback(async (page: number = 0) => {
    setLoading(true);
    try {
      const params: Record<string, any> = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (evalFilter.status) params.status = evalFilter.status;
      if (evalFilter.search) params.search = evalFilter.search;
      if (evalSort.sortBy) params.sortBy = evalSort.sortBy;
      if (evalSort.sortDir) params.sortDir = evalSort.sortDir;
      const data = await WindAlertAdminService.fetchEvaluationsPaginated(params).catch(() => ({ rows: [], total: 0 }));
      setEvaluations(data.rows || []);
      setEvalTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [evalFilter, evalSort]);

  const load = useCallback(async () => {
    await loadEvaluations(evalPage);
  }, [loadEvaluations, evalPage]);

  // Reload evaluations when filters or page change
  useEffect(() => { loadEvaluations(evalPage); }, [loadEvaluations, evalPage]);

  const evalTotalPages = Math.ceil(evalTotal / PAGE_SIZE);

  const applyEvalFilter = () => { setEvalPage(0); loadEvaluations(0); };

  const toggleSort = (col: string) => {
    setEvalSort((prev) => ({
      sortBy: col,
      sortDir: prev.sortBy === col && prev.sortDir === "desc" ? "asc" : "desc",
    }));
    setEvalPage(0);
  };

  useEffect(() => { load(); }, [load]);

  const evalStats = useMemo(() => {
    const total = evaluations.length;
    const delivered = evaluations.filter((e) => e.delivered).length;
    const pending = evaluations.filter((e) => !e.delivered).length;
    const verified = evaluations.filter((e) => e.verified === true).length;
    const busted = evaluations.filter((e) => e.verified === false).length;
    const uniqueUsers = new Set(evaluations.map((e) => e.userId)).size;
    const uniqueSpots = new Set(evaluations.map((e) => e.spotId)).size;
    return { total, delivered, pending, verified, busted, uniqueUsers, uniqueSpots };
  }, [evaluations]);

  const updateForm = <K extends keyof TriggerWindAlertRequest>(key: K, value: TriggerWindAlertRequest[K]) =>
    setTriggerForm((prev) => ({ ...prev, [key]: value }));

  const canSubmit = triggerForm.userId > 0 && triggerForm.spotId > 0 && !!triggerForm.modelKey
    && !!peakWhen && Number.isFinite(new Date(peakWhen).getTime());

  const handleTrigger = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || triggering) return;
    setTriggering(true);
    setTriggerStatus(null);
    try {
      const payload: TriggerWindAlertRequest = { ...triggerForm, peakTimeLocal: peakWhen, timestamp: undefined };
      const res = await WindAlertAdminService.trigger(payload);
      const when = new Date(res.timestamp).toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", timeZoneName: "short",
      });
      setTriggerStatus({
        kind: "ok",
        message: `Delivered to user #${res.userId} (${res.userEmail || "no email"}) for ${res.spotName} — ${res.knots}/${res.gust} kts, ${res.modelKey}, peak ${when}.`,
      });
      load();
    } catch (e: any) {
      setTriggerStatus({ kind: "err", message: e?.userMessage || e?.message || e?.response?.data?.error || "Trigger failed" });
    } finally {
      setTriggering(false);
    }
  };

  // Digest test state
  const [digestEntries, setDigestEntries] = useState<(DigestEntry & { spotName?: string })[]>([
    { spotId: 0, modelKey: "gfs", knots: 18, gust: 24, peakTimeLocal: defaultPeakWhen() },
  ]);
  const [digestSelectedUser, setDigestSelectedUser] = useState<User | null>(null);
  const [digestUserId, setDigestUserId] = useState(0);
  const [digestSending, setDigestSending] = useState(false);
  const [digestStatus, setDigestStatus] = useState<{ kind: "ok" | "err"; message: string } | null>(null);

  const addDigestEntry = () => {
    setDigestEntries((prev) => [...prev, { spotId: 0, modelKey: "gfs", knots: 18, gust: 24, peakTimeLocal: defaultPeakWhen() }]);
  };
  const removeDigestEntry = (idx: number) => {
    setDigestEntries((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateDigestEntry = (idx: number, field: string, value: any) => {
    setDigestEntries((prev) => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  const handleDigestSpotSelect = (idx: number, s: SpotSearchResult) => {
    setDigestEntries((prev) => prev.map((e, i) => i === idx ? { ...e, spotId: s.id, spotName: s.name } : e));
  };

  const canSubmitDigest = digestUserId > 0 && digestEntries.length > 0 && digestEntries.every((e) => e.spotId > 0);

  const handleDigestTrigger = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitDigest || digestSending) return;
    setDigestSending(true);
    setDigestStatus(null);
    try {
      const res = await WindAlertAdminService.triggerDigest(digestUserId, digestEntries);
      setDigestStatus({ kind: "ok", message: `Digest email sent to ${res.email} with ${res.entries} entries.` });
    } catch (err: any) {
      setDigestStatus({ kind: "err", message: err?.userMessage || err?.message || "Digest trigger failed" });
    } finally {
      setDigestSending(false);
    }
  };

  return (
    <div className="min-h-full bg-gray-900 py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <BellAlertIcon className="size-7 text-blue-400" />
            Wind Alerts
          </h1>
          <p className="mt-1 text-sm text-gray-400">
            Monitor the wind alert pipeline: forecast evaluations, delivery status, and manual testing tools.
          </p>
          <p className="mt-0.5 text-xs text-gray-600">
            Pipeline A (Ingest) checks forecasts every 2h &rarr; Pipeline B (Delivery) sends digest notifications hourly &rarr; Pipeline C (Verify) checks accuracy daily.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg bg-gray-800 border border-gray-700 px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
        >
          <ArrowPathIcon className={`size-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6">
        <StatCard label="Evaluations" value={evalStats.total} />
        <StatCard label="Delivered" value={evalStats.delivered} accent="green" />
        <StatCard label="Pending" value={evalStats.pending} accent="amber" />
        <StatCard label="Verified" value={evalStats.verified} accent="emerald" />
        <StatCard label="Busted" value={evalStats.busted} accent="red" />
        <StatCard label="Users" value={evalStats.uniqueUsers} />
        <StatCard label="Spots" value={evalStats.uniqueSpots} />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700 mb-6">
        <nav className="flex gap-6">
          <TabButton active={tab === "evaluations"} onClick={() => setTab("evaluations")} icon={<ChartBarIcon className="size-4" />} label="Evaluations" />
          <TabButton active={tab === "trigger"} onClick={() => setTab("trigger")} icon={<PaperAirplaneIcon className="size-4" />} label="Manual Trigger" />
        </nav>
      </div>

      {/* Tab content */}
      {tab === "evaluations" && (
        <section className="rounded-xl bg-gray-800 border border-gray-700 overflow-hidden">
          <header className="px-5 py-3 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-white">Pipeline Evaluations</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Each row = one forecast check for a user's wind alert. Created by Pipeline A (Ingest), delivered by Pipeline B.
                </p>
              </div>
              <span className="text-xs text-gray-500">{evalTotal} total</span>
            </div>
            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3 mt-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Status</label>
                <select value={evalFilter.status} onChange={(e) => setEvalFilter((f) => ({ ...f, status: e.target.value }))}
                  className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500">
                  <option value="">All</option>
                  <option value="pending">Pending (not yet sent)</option>
                  <option value="delivered">Delivered (sent to user)</option>
                  <option value="admin_test">Admin test</option>
                  <option value="verified">Verified (forecast was correct)</option>
                  <option value="busted">Busted (forecast was wrong)</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Search</label>
                <input type="text" value={evalFilter.search} onChange={(e) => setEvalFilter((f) => ({ ...f, search: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && applyEvalFilter()}
                  placeholder="Spot name or user email..."
                  className="w-56 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500" />
              </div>
              <button onClick={applyEvalFilter}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded transition-colors">
                Filter
              </button>
              {(evalFilter.status || evalFilter.search) && (
                <button onClick={() => { setEvalFilter({ status: "", search: "" }); setEvalPage(0); }}
                  className="px-2 py-1.5 text-gray-400 hover:text-white text-xs transition-colors">
                  Clear
                </button>
              )}
            </div>
          </header>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700 text-sm">
              <thead className="bg-gray-800/50">
                <tr>
                  <Th sortKey="createdAt" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>Created</Th>
                  <Th sortKey="evaluatedAt" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>Last Checked</Th>
                  <Th sortKey="alertType" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>Type</Th>
                  <Th sortKey="userId" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>User</Th>
                  <Th sortKey="spotName" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>Spot</Th>
                  <Th sortKey="modelKey" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>Model</Th>
                  <Th sortKey="peakKnots" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>Wind / Gust</Th>
                  <Th sortKey="peakWindDirection" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>Dir</Th>
                  <Th sortKey="minKnotsThreshold" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>Min. Wind</Th>
                  <Th sortKey="alertDate" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>Forecast Day</Th>
                  <Th sortKey="delivered" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>Delivery</Th>
                  <Th sortKey="deliveredAt" sortBy={evalSort.sortBy} sortDir={evalSort.sortDir} onSort={toggleSort}>Delivered At</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {loading && <EmptyRow cols={12} text="Loading..." />}
                {!loading && evaluations.length === 0 && <EmptyRow cols={12} text="No evaluations yet. Pipeline A has not run." />}
                {!loading && evaluations.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-700/30 transition-colors">
                    <Td>{row.createdAt ? formatDateTime(row.createdAt) : formatDateTime(row.evaluatedAt)}</Td>
                    <Td><span className="text-gray-500">{formatDateTime(row.evaluatedAt)}</span></Td>
                    <Td><TypePill type={row.alertType} /></Td>
                    <Td>
                      <div className="font-medium text-gray-200">#{row.userId}</div>
                      <div className="text-xs text-gray-500 truncate max-w-[160px]">{row.userEmail || "—"}</div>
                    </Td>
                    <Td>
                      <div className="font-medium text-gray-200">{row.spotName || `#${row.spotId}`}</div>
                      <div className="text-xs text-gray-500">id {row.spotId}</div>
                    </Td>
                    <Td><span className="text-gray-400">{row.modelKey}</span></Td>
                    <Td>
                      <span className="font-semibold text-white">{Math.round(row.peakKnots)}</span>
                      {row.peakGustKnots && <span className="text-gray-500">/{Math.round(row.peakGustKnots)}</span>}
                      <span className="text-gray-500 ml-1">kts</span>
                    </Td>
                    <Td><span className="text-gray-400">{row.peakWindDirection != null ? `${degreesToArrow(row.peakWindDirection)} ${degreesToCardinal(row.peakWindDirection)}` : "—"}</span></Td>
                    <Td><span className="text-gray-400">{row.minKnotsThreshold} kts</span></Td>
                    <Td>
                      <div className="text-gray-300">{row.dayName || "—"}</div>
                      <div className="text-xs text-gray-500">{formatDate(row.alertDate)}</div>
                    </Td>
                    <Td><DeliveryBadge delivered={row.delivered} channel={row.deliveryChannel} /></Td>
                    <Td><span className="text-gray-400">{row.deliveredAt ? formatDateTime(row.deliveredAt) : "—"}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {evalTotalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-700">
              <span className="text-xs text-gray-500">
                Showing {evalPage * PAGE_SIZE + 1}–{Math.min((evalPage + 1) * PAGE_SIZE, evalTotal)} of {evalTotal}
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => setEvalPage((p) => Math.max(0, p - 1))} disabled={evalPage === 0}
                  className="px-3 py-1 text-xs font-medium rounded bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Previous
                </button>
                <span className="text-xs text-gray-400">Page</span>
                <input
                  type="text"
                  value={pageInput || String(evalPage + 1)}
                  onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ""))}
                  onFocus={() => setPageInput(String(evalPage + 1))}
                  onBlur={() => {
                    const p = Math.max(1, Math.min(evalTotalPages, parseInt(pageInput) || 1));
                    setEvalPage(p - 1);
                    setPageInput("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const p = Math.max(1, Math.min(evalTotalPages, parseInt(pageInput) || 1));
                      setEvalPage(p - 1);
                      setPageInput("");
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  className="w-12 px-1.5 py-0.5 text-center text-xs bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-400">of {evalTotalPages}</span>
                <button onClick={() => setEvalPage((p) => Math.min(evalTotalPages - 1, p + 1))} disabled={evalPage >= evalTotalPages - 1}
                  className="px-3 py-1 text-xs font-medium rounded bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  Next
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {tab === "trigger" && (
        <section className="rounded-xl bg-gray-800 border border-gray-700 overflow-hidden">
          <header className="px-5 py-3 border-b border-gray-700">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <PaperAirplaneIcon className="size-5 text-blue-400" />
              Send manual wind alert
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Fires through the same delivery path as the pipeline (push + email).
            </p>
          </header>
          <form onSubmit={handleTrigger} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-1">
              <label className="block text-xs font-semibold text-gray-400 mb-1">
                User<span className="text-red-400 ml-0.5">*</span>
              </label>
              {selectedUser ? (
                <SelectedChip
                  avatar={selectedUser.profilepicid ? `${ENDPOINTS.IMAGES.PROFILE}/${selectedUser.profilepicid}` : undefined}
                  primary={selectedUser.name || selectedUser.username || `User #${selectedUser.id}`}
                  secondary={`${selectedUser.username || ""} · id ${selectedUser.id}`}
                  onClear={() => { setSelectedUser(null); setTriggerForm((prev) => ({ ...prev, userId: 0 })); }}
                />
              ) : (
                <div className="-mb-6"><UserSearchInput theme="dark" onUserSelect={handleUserSelect} adminMode /></div>
              )}
            </div>
            <div className="md:col-span-1">
              <label className="block text-xs font-semibold text-gray-400 mb-1">
                Spot<span className="text-red-400 ml-0.5">*</span>
              </label>
              {selectedSpot ? (
                <SelectedChip
                  primary={selectedSpot.name}
                  secondary={`${selectedSpot.countryName} (${selectedSpot.alpha2}) · id ${selectedSpot.id}`}
                  onClear={() => { setSelectedSpot(null); setTriggerForm((prev) => ({ ...prev, spotId: 0 })); }}
                />
              ) : (
                <SpotSearchInput onSpotSelect={handleSpotSelect} selectedSpot={null} />
              )}
            </div>

            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-6 gap-4">
              <DarkField label="Model" required>
                <select value={triggerForm.modelKey} onChange={(e) => updateForm("modelKey", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {MODEL_OPTIONS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </DarkField>
              <DarkField label="Wind (kts)">
                <input type="number" min={1} value={triggerForm.knots ?? ""} placeholder="22"
                  onChange={(e) => updateForm("knots", e.target.value === "" ? undefined : Number(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </DarkField>
              <DarkField label="Gust (kts)">
                <input type="number" min={1} value={triggerForm.gust ?? ""} placeholder="28"
                  onChange={(e) => updateForm("gust", e.target.value === "" ? undefined : Number(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </DarkField>
              <DarkField label="Peak time (spot local)" required>
                <input type="datetime-local" value={peakWhen} onChange={(e) => setPeakWhen(e.target.value)} required
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </DarkField>

              <div className="md:col-span-6 flex items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-gray-400">
                  <input type="checkbox" checked={!!triggerForm.reminder}
                    onChange={(e) => updateForm("reminder", e.target.checked)}
                    className="rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500" />
                  Send as "reminder"
                </label>
                <div className="flex-1" />
                <button type="submit" disabled={!canSubmit || triggering}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <PaperAirplaneIcon className="size-4" />
                  {triggering ? "Sending..." : "Fire wind alert"}
                </button>
              </div>

              {triggerStatus && (
                <div className={`md:col-span-6 flex items-start gap-2 rounded-lg p-3 text-sm ${
                  triggerStatus.kind === "ok" ? "bg-green-900/30 text-green-300 border border-green-800" : "bg-red-900/30 text-red-300 border border-red-800"
                }`}>
                  {triggerStatus.kind === "ok" ? <CheckCircleIcon className="size-5 shrink-0 mt-0.5" /> : <XCircleIcon className="size-5 shrink-0 mt-0.5" />}
                  <span>{triggerStatus.message}</span>
                </div>
              )}
            </div>
          </form>
        </section>
      )}

      {/* Digest email test */}
      {tab === "trigger" && (
        <section className="mt-6 rounded-xl bg-gray-800 border border-gray-700 overflow-hidden">
          <header className="px-5 py-3 border-b border-gray-700">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <BellAlertIcon className="size-5 text-amber-400" />
              Test digest email
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Send a test digest email with multiple spot/date entries. No evaluations or delivery logs are created.
            </p>
          </header>
          <form onSubmit={handleDigestTrigger} className="p-5 space-y-4">
            {/* User */}
            <div className="max-w-sm">
              <label className="block text-xs font-semibold text-gray-400 mb-1">
                Recipient<span className="text-red-400 ml-0.5">*</span>
              </label>
              {digestSelectedUser ? (
                <SelectedChip
                  avatar={digestSelectedUser.profilepicid ? `${ENDPOINTS.IMAGES.PROFILE}/${digestSelectedUser.profilepicid}` : undefined}
                  primary={digestSelectedUser.name || digestSelectedUser.username || `User #${digestSelectedUser.id}`}
                  secondary={`${digestSelectedUser.username || ""} · id ${digestSelectedUser.id}`}
                  onClear={() => { setDigestSelectedUser(null); setDigestUserId(0); }}
                />
              ) : (
                <div className="-mb-6"><UserSearchInput theme="dark" onUserSelect={(u: User) => { setDigestSelectedUser(u); setDigestUserId(u.id); }} adminMode /></div>
              )}
            </div>

            {/* Entries */}
            <div className="space-y-3">
              {digestEntries.map((entry, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end p-3 rounded-lg bg-gray-750 border border-gray-700">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-400 mb-1">Spot</label>
                    {entry.spotId > 0 ? (
                      <SelectedChip
                        primary={entry.spotName || `Spot #${entry.spotId}`}
                        onClear={() => updateDigestEntry(idx, "spotId", 0)}
                      />
                    ) : (
                      <SpotSearchInput onSpotSelect={(s: SpotSearchResult) => handleDigestSpotSelect(idx, s)} selectedSpot={null} />
                    )}
                  </div>
                  <DarkField label="Model">
                    <select value={entry.modelKey} onChange={(e) => updateDigestEntry(idx, "modelKey", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      {MODEL_OPTIONS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                    </select>
                  </DarkField>
                  <DarkField label="Wind">
                    <input type="number" min={1} value={entry.knots ?? ""} placeholder="18"
                      onChange={(e) => updateDigestEntry(idx, "knots", e.target.value === "" ? undefined : Number(e.target.value))}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </DarkField>
                  <DarkField label="Gust">
                    <input type="number" min={1} value={entry.gust ?? ""} placeholder="24"
                      onChange={(e) => updateDigestEntry(idx, "gust", e.target.value === "" ? undefined : Number(e.target.value))}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </DarkField>
                  <DarkField label="Peak time">
                    <input type="datetime-local" value={entry.peakTimeLocal || ""} onChange={(e) => updateDigestEntry(idx, "peakTimeLocal", e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </DarkField>
                  <div className="flex items-end">
                    {digestEntries.length > 1 && (
                      <button type="button" onClick={() => removeDigestEntry(idx)} className="text-red-400 hover:text-red-300 p-2" title="Remove">
                        <XCircleIcon className="size-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <button type="button" onClick={addDigestEntry}
                className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors">
                + Add entry
              </button>
              <div className="flex-1" />
              <button type="submit" disabled={!canSubmitDigest || digestSending}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <BellAlertIcon className="size-4" />
                {digestSending ? "Sending..." : "Send digest email"}
              </button>
            </div>

            {digestStatus && (
              <div className={`flex items-start gap-2 rounded-lg p-3 text-sm ${
                digestStatus.kind === "ok" ? "bg-green-900/30 text-green-300 border border-green-800" : "bg-red-900/30 text-red-300 border border-red-800"
              }`}>
                {digestStatus.kind === "ok" ? <CheckCircleIcon className="size-5 shrink-0 mt-0.5" /> : <XCircleIcon className="size-5 shrink-0 mt-0.5" />}
                <span>{digestStatus.message}</span>
              </div>
            )}
          </form>
        </section>
      )}
    </div>
  );
};

// =========================================================================
// Sub-components
// =========================================================================

const StatCard: React.FC<{ label: string; value: number; accent?: string }> = ({ label, value, accent }) => {
  const accentColor = accent === "green" ? "text-green-400" : accent === "amber" ? "text-amber-400"
    : accent === "emerald" ? "text-emerald-400" : accent === "red" ? "text-red-400" : "text-white";
  return (
    <div className="rounded-lg bg-gray-800 border border-gray-700 p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${accentColor}`}>{value}</div>
    </div>
  );
};

const TabButton: React.FC<{ active: boolean; onClick: () => void; icon: React.ReactNode; label: string }> = ({ active, onClick, icon, label }) => (
  <button onClick={onClick}
    className={`inline-flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
      active ? "border-blue-500 text-blue-400" : "border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600"
    }`}>
    {icon}{label}
  </button>
);

const DarkField: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-400 mb-1">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const SelectedChip: React.FC<{ avatar?: string; primary: string; secondary?: string; onClear: () => void }> = ({ avatar, primary, secondary, onClear }) => (
  <div className="flex items-center gap-3 rounded-lg border border-gray-600 bg-gray-700 px-3 py-2">
    {avatar ? (
      <img src={avatar} alt="" className="size-9 rounded-full object-cover bg-gray-600"
        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
    ) : (
      <div className="size-9 rounded-full bg-blue-600/30 flex items-center justify-center text-blue-300 font-semibold text-sm">
        {primary.charAt(0).toUpperCase()}
      </div>
    )}
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-gray-200 truncate">{primary}</div>
      {secondary && <div className="text-xs text-gray-500 truncate">{secondary}</div>}
    </div>
    <button type="button" onClick={onClear} className="text-gray-500 hover:text-gray-300" title="Clear">
      <XMarkIcon className="size-4" />
    </button>
  </div>
);

const Th: React.FC<{ children: React.ReactNode; sortKey?: string; sortBy?: string; sortDir?: string; onSort?: (key: string) => void }> = ({ children, sortKey, sortBy, sortDir, onSort }) => {
  const active = sortKey && sortBy === sortKey;
  const arrow = active ? (sortDir === "asc" ? " ↑" : " ↓") : "";
  return (
    <th
      className={`px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide ${sortKey ? "cursor-pointer select-none hover:text-gray-300" : ""} ${active ? "text-blue-400" : "text-gray-500"}`}
      onClick={() => sortKey && onSort?.(sortKey)}
    >
      {children}{arrow}
    </th>
  );
};

const Td: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <td className="px-4 py-2.5 align-top text-gray-300">{children}</td>
);

const EmptyRow: React.FC<{ cols: number; text: string }> = ({ cols, text }) => (
  <tr><td colSpan={cols} className="px-5 py-8 text-center text-gray-500">{text}</td></tr>
);

const TypePill: React.FC<{ type: string }> = ({ type }) => {
  const styles = type === "reminder" ? "bg-amber-900/40 text-amber-300 border-amber-700"
    : type === "admin_test" ? "bg-purple-900/40 text-purple-300 border-purple-700"
    : type === "morning_of" ? "bg-blue-900/40 text-blue-300 border-blue-700"
    : "bg-cyan-900/40 text-cyan-300 border-cyan-700";
  const label = type === "reminder" ? "Reminder" : type === "admin_test" ? "Admin test"
    : type === "morning_of" ? "Morning-of" : "Early";
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${styles}`}>{label}</span>;
};

const DELIVERY_LABELS: Record<string, string> = {
  digest: "Email + Push",
  "push+email": "Email + Push",
  push_only: "Push only",
  email: "Email only",
  push: "Push only",
};
const DeliveryBadge: React.FC<{ delivered: boolean; channel: string | null }> = ({ delivered, channel }) => {
  if (!delivered) return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-900/40 text-amber-300 border border-amber-700">Pending</span>;
  if (channel === "expired") return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-700 text-gray-400 border border-gray-600">Expired</span>;
  const label = (channel && DELIVERY_LABELS[channel]) || channel || "Sent";
  return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-900/40 text-green-300 border border-green-700">{label}</span>;
};

const VerifiedBadge: React.FC<{ verified: boolean | null; actual: number | null }> = ({ verified, actual }) => {
  if (verified === null) return <span className="text-gray-600">—</span>;
  if (verified) return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-900/40 text-emerald-300 border border-emerald-700">
      <CheckCircleIcon className="size-3.5" />{actual != null ? `${Math.round(actual)} kts` : "Yes"}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-red-900/40 text-red-300 border border-red-700">
      <XCircleIcon className="size-3.5" />{actual != null ? `${Math.round(actual)} kts` : "Busted"}
    </span>
  );
};

export default WindAlertsPage;
